import { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useElapsedSeconds, formatElapsed } from '../../hooks/useElapsedSeconds';

export type SmartLoaderPhaseStatus = 'pending' | 'active' | 'done' | 'failed';

export interface SmartLoaderPhase {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  /** Required for `elapsed` mode — the second at which this phase becomes active. */
  startSec?: number;
  /** When ANY phase sets this, elapsed-derivation is bypassed and statuses are used verbatim. */
  status?: SmartLoaderPhaseStatus;
}

export type SmartLoaderProgress =
  | { mode: 'elapsed'; expectedSec: number }
  | { mode: 'indeterminate' }
  | { mode: 'count'; completed: number; total: number };

export type SmartLoaderAccent = 'primary' | 'violet' | 'orange';

export interface SmartLoaderProps {
  title: string;
  subtitle?: string;
  phases: SmartLoaderPhase[];
  progress: SmartLoaderProgress;
  /** Drives the internal elapsed ticker. Callers pass a boolean, never a hook. */
  active: boolean;
  accent?: SmartLoaderAccent;
  footnote?: string;
  /** Suppress render for the first N ms to avoid a flash on cached/fast responses. */
  delayMs?: number;
  className?: string;
}

// Literal class strings per accent — Tailwind purges on literal matches, so these
// cannot be constructed dynamically.
const ACCENTS: Record<
  SmartLoaderAccent,
  {
    card: string;
    headerBar: string;
    iconWrap: string;
    spinner: string;
    ping: string;
    title: string;
    activeText: string;
    dot: string;
    barTrack: string;
    barFill: string;
  }
> = {
  primary: {
    card: 'border-primary-200 from-primary-50 via-white to-primary-50/40',
    headerBar: 'border-primary-100',
    iconWrap: 'bg-primary-100 text-primary-600',
    spinner: 'text-primary-600',
    ping: 'ring-primary-200/50',
    title: 'text-primary-900',
    activeText: 'text-primary-900',
    dot: 'bg-primary-500',
    barTrack: 'bg-primary-100',
    barFill: 'from-primary-500 to-primary-600',
  },
  violet: {
    card: 'border-violet-200 from-violet-50 via-white to-violet-50/40',
    headerBar: 'border-violet-100',
    iconWrap: 'bg-violet-100 text-violet-600',
    spinner: 'text-violet-600',
    ping: 'ring-violet-200/50',
    title: 'text-violet-900',
    activeText: 'text-violet-900',
    dot: 'bg-violet-500',
    barTrack: 'bg-violet-100',
    barFill: 'from-violet-500 to-violet-600',
  },
  orange: {
    card: 'border-orange-200 from-orange-50 via-white to-orange-50/40',
    headerBar: 'border-orange-100',
    iconWrap: 'bg-orange-100 text-orange-600',
    spinner: 'text-orange-600',
    ping: 'ring-orange-200/50',
    title: 'text-orange-900',
    activeText: 'text-orange-900',
    dot: 'bg-orange-500',
    barTrack: 'bg-orange-100',
    barFill: 'from-orange-500 to-orange-600',
  },
};

/**
 * Staged loader for work with real, narratable phases. Three progress modes:
 *   - elapsed: simulated bar capped at 95%, phase derived from elapsed time
 *   - count:   real {completed}/{total} bar (e.g. peer fan-out)
 *   - indeterminate: honest sweeping bar, no fabricated percentage
 * Any phase carrying an explicit `status` overrides elapsed-derivation entirely.
 */
export default function SmartLoader({
  title,
  subtitle,
  phases,
  progress,
  active,
  accent = 'primary',
  footnote,
  delayMs = 250,
  className = '',
}: SmartLoaderProps) {
  const elapsedSec = useElapsedSeconds(active);

  const [show, setShow] = useState(delayMs === 0);
  useEffect(() => {
    if (delayMs === 0) return;
    const t = setTimeout(() => setShow(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  if (!show) return null;

  const c = ACCENTS[accent];

  // Phase status: explicit statuses (count mode) win; otherwise derive from elapsed.
  const hasExplicit = phases.some((p) => p.status);
  const activeIdx = hasExplicit
    ? -1
    : phases.reduce((idx, p, i) => (elapsedSec >= (p.startSec ?? 0) ? i : idx), 0);

  const statusOf = (p: SmartLoaderPhase, i: number): SmartLoaderPhaseStatus => {
    if (p.status) return p.status;
    if (i < activeIdx) return 'done';
    if (i === activeIdx) return 'active';
    return 'pending';
  };

  const barPct =
    progress.mode === 'elapsed'
      ? Math.min(95, Math.round((elapsedSec / Math.max(1, progress.expectedSec)) * 95))
      : progress.mode === 'count'
      ? progress.total > 0
        ? Math.round((progress.completed / progress.total) * 100)
        : 0
      : null; // indeterminate

  return (
    <div
      className={`rounded-xl border bg-gradient-to-br overflow-hidden shadow-sm ${c.card} ${className}`}
      aria-busy="true"
      aria-live="polite"
    >
      {/* Header */}
      <div className={`px-5 py-4 border-b bg-white/60 backdrop-blur ${c.headerBar}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`relative h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${c.iconWrap}`}>
              <Loader2 className={`h-5 w-5 animate-spin ${c.spinner}`} />
              <div className={`absolute inset-0 rounded-full ring-4 animate-ping ${c.ping}`} />
            </div>
            <div>
              <h3 className={`text-sm font-semibold ${c.title}`}>{title}</h3>
              {subtitle && <p className="text-xs text-neutral-600 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[11px] uppercase tracking-wide text-neutral-500">
              {progress.mode === 'count' ? 'Progress' : 'Elapsed'}
            </div>
            <div className={`text-sm font-mono font-semibold tabular-nums ${c.title}`}>
              {progress.mode === 'count'
                ? `${progress.completed} of ${progress.total}`
                : formatElapsed(elapsedSec)}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className={`mt-3 w-full rounded-full h-1.5 overflow-hidden relative ${c.barTrack}`}>
          {barPct === null ? (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent animate-shimmer" />
          ) : (
            <div
              className={`h-full rounded-full bg-gradient-to-r transition-all duration-1000 ease-out ${c.barFill}`}
              style={{ width: `${barPct}%` }}
            />
          )}
        </div>
      </div>

      {/* Phase steps */}
      <div className="px-5 py-4">
        <div className="space-y-2.5">
          {phases.map((p, i) => {
            const Icon = p.icon;
            const st = statusOf(p, i);
            return (
              <div
                key={p.id}
                className={`flex items-start gap-3 transition-opacity duration-300 ${
                  st === 'pending' ? 'opacity-40' : 'opacity-100'
                }`}
              >
                <div
                  className={`mt-0.5 h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    st === 'done'
                      ? 'bg-green-100 text-green-600'
                      : st === 'failed'
                      ? 'bg-red-100 text-red-600'
                      : st === 'active'
                      ? c.iconWrap
                      : 'bg-neutral-100 text-neutral-400'
                  }`}
                >
                  {st === 'done' ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : st === 'failed' ? (
                    <XCircle className="h-4 w-4" />
                  ) : (
                    <Icon className={`h-3.5 w-3.5 ${st === 'active' ? 'animate-pulse' : ''}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-xs font-semibold ${
                      st === 'done'
                        ? 'text-green-700'
                        : st === 'failed'
                        ? 'text-red-700'
                        : st === 'active'
                        ? c.activeText
                        : 'text-neutral-500'
                    }`}
                  >
                    {p.label}
                    {st === 'active' && (
                      <span className={`ml-2 inline-flex items-center gap-0.5 text-[10px] font-normal ${c.spinner}`}>
                        <span className={`w-1 h-1 rounded-full animate-pulse ${c.dot}`} />
                        in progress
                      </span>
                    )}
                  </div>
                  {p.hint && <p className="text-[11px] text-neutral-500 mt-0.5">{p.hint}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {footnote && (
        <div className="px-5 py-3 bg-neutral-50/80 border-t border-neutral-100 flex items-center gap-2">
          <Loader2 className="h-3 w-3 text-neutral-400 animate-spin" />
          <p className="text-[11px] text-neutral-600">{footnote}</p>
        </div>
      )}
    </div>
  );
}
